import React, { useState, useRef, useCallback } from 'react';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import { analyticsUtils } from 'utils/analytics-utils';
import { useIntersection } from 'common/hooks';
import {
  COLLECTIONS_STORE_NAME,
  FacetValue,
  FETCH_COLLECTIONS,
  KitchenCollection,
  selectAvailableCollections,
  selectStyleFacet,
} from 'modules/collections';
import {
  Button,
  GalleryCardContent,
  GalleryCatalog,
  KitchenDownloadCatalog,
  Section,
  SelectionGroup,
  SelectionGroupOption,
  SelectionGroupValue,
} from 'common/components';
import { KITCHEN_ELEMENT_BLOCK } from '../../../kitchen-constants';
import {
  newKitchenGallerySectionMoreBtn,
  newKitchenGallerySectionTabs,
} from './new-kitchen-gallery-section-styles';

const INITIAL_IMAGES_PER_PAGE = 5;
const IMAGES_PER_PAGE = 6;

const getSelectionGroupList = (styles: FacetValue[]): SelectionGroupOption[] =>
  styles.map(({ name, value }) => ({
    text: value as string,
    value: name,
  }));

export const NewKitchenGallerySection: React.FC = () => {
  const { collections, dispatch } = useStoreon<State>(COLLECTIONS_STORE_NAME);
  const productRef = useRef<HTMLAnchorElement[]>([]);
  const collectionList = selectAvailableCollections(collections) as KitchenCollection[];
  const styles = selectStyleFacet(collections);
  const selectionGroupList = getSelectionGroupList(styles?.values || []);

  const [activeSelectionGroup, setActiveSelectionGroup] = useState<string>('all');
  const [imagePerPage, setImagePerPage] = useState<number>(INITIAL_IMAGES_PER_PAGE);

  const { offset, productsCount } = collections;

  useIntersection(productRef, el => {
    const position = el.getAttribute('data-position');

    if (!position) {
      return;
    }

    const numberPosition = Number(position);
    const collection = collectionList[numberPosition];

    analyticsUtils.sendEvent('GA:impressions', {
      elementType: 'product',
      elementBlock: `${KITCHEN_ELEMENT_BLOCK} / Catalog`,
      division: 'Кухни',
      subdivision: 'Проект кухни как пример',
      komplektyId: collection.productId,
      productName: collection.displayedName,
      productId: collection.productId,
      productPrice: collection.price.price,
      productPosition: numberPosition + 1,
      productTag: undefined,
    });
  });

  const handleTabChange = (e: React.MouseEvent, value: SelectionGroupValue) => {
    if (value !== activeSelectionGroup) {
      setImagePerPage(INITIAL_IMAGES_PER_PAGE);
      setActiveSelectionGroup(value as string);

      dispatch(FETCH_COLLECTIONS.clear);
      dispatch(FETCH_COLLECTIONS.init, {
        url: 'get-kitchen-price',
        offset: 0,
        limit: INITIAL_IMAGES_PER_PAGE,
        variantId: value,
        landingType: 'new-kitchen',
      });

      const activeOption: SelectionGroupOption | undefined = selectionGroupList.find(
        (option: SelectionGroupOption) => option.value === value
      );
      if (activeOption) {
        analyticsUtils.clickButtonEvent(
          `${KITCHEN_ELEMENT_BLOCK} / Catalog`,
          activeOption.text,
          'Каталог кухонь'
        );
      }
    }
  };

  const handleClickShowMoreBtn = () => {
    setImagePerPage(imagePerPage + IMAGES_PER_PAGE);
    dispatch(FETCH_COLLECTIONS.init, {
      url: 'get-kitchen-price',
      limit: IMAGES_PER_PAGE,
      offset: offset === 0 ? INITIAL_IMAGES_PER_PAGE : offset + IMAGES_PER_PAGE,
      variantId: activeSelectionGroup,
      landingType: 'new-kitchen',
    });
    analyticsUtils.clickButtonEvent(`${KITCHEN_ELEMENT_BLOCK} / Catalog`, 'Показать ещё', 'Каталог кухонь');
  };

  const handleCardClick = useCallback((collection: KitchenCollection, position: number) => {
    analyticsUtils.sendEvent('GA:clickProduct', {
      elementType: 'product',
      elementBlock: `${KITCHEN_ELEMENT_BLOCK} / Catalog`,
      elementName: 'productNameLink',
      division: 'Кухни',
      subdivision: 'Проект кухни как пример',
      komplektyId: collection.productId,
      productName: collection.displayedName,
      productId: collection.productId,
      productPrice: collection.price,
      productPosition: position + 1,
      productTag: undefined,
    });
  }, []);

  return (
    <Section>
      <SelectionGroup
        className={newKitchenGallerySectionTabs}
        active={activeSelectionGroup}
        onChange={handleTabChange}
        optionList={selectionGroupList}
        rounded
      />

      {collectionList.length > 0 && (
        <GalleryCatalog
          catalog={collectionList as KitchenCollection[]}
          cardRefCb={(el: HTMLAnchorElement | null) => {
            const position = el?.getAttribute('data-position');
            if (position && el) {
              productRef.current[+position] = el;
            }
          }}
          downloadCatalog={
            <KitchenDownloadCatalog
              elementBlock={`${KITCHEN_ELEMENT_BLOCK} / Catalog`}
              elementName="Каталог кухонь"
              isNewKitchen
            />
          }
          renderCardContent={({ displayedName, price }) => <GalleryCardContent displayedName={displayedName} price={price.price} />}
          onCardClick={handleCardClick}
          isNew
        />
      )}

      {IMAGES_PER_PAGE + offset <= productsCount && (
        <Button
          className={newKitchenGallerySectionMoreBtn}
          rounded
          onClick={handleClickShowMoreBtn}
          adaptive={false}>
          Показать ещё
        </Button>
      )}
    </Section>
  );
};
